const PHASE_PRE = "pre";
const PHASE_EARLY = "early";
const PHASE_LIVE = "live";
const PHASE_ENDED = "ended";

const SECONDS_PER_MINUTE = 60;
const MAX_CHAT_BUFFER_MINUTES = 1440;
const KEY_SERIES_COMPLETED_AT = "series_completed_at";

function timestampField(eventData, field) {
  const value = Number(eventData?.[field] ?? 0);

  return Number.isFinite(value) && value > 0 ? value : 0;
}

function isTruthyFlag(eventData, field) {
  const value = eventData?.[field];

  return Boolean(value) && value !== "0";
}

function occurrenceCompletedAt(eventData) {
  const completedAt = timestampField(eventData, "completed_at");
  const start = timestampField(eventData, "start_timestamp");

  return completedAt >= start ? completedAt : 0;
}

function explicitEndAt(eventData) {
  const occurrenceCompletedAt_ = occurrenceCompletedAt(eventData);
  const seriesCompletedAt = timestampField(eventData, KEY_SERIES_COMPLETED_AT);

  if (occurrenceCompletedAt_ > 0 && seriesCompletedAt > 0) {
    return Math.min(occurrenceCompletedAt_, seriesCompletedAt);
  }

  return Math.max(occurrenceCompletedAt_, seriesCompletedAt);
}

function hasExplicitlyEnded(eventData, now) {
  const explicitEnd = explicitEndAt(eventData);

  return explicitEnd > 0 && now >= explicitEnd;
}

function endedAt(eventData) {
  const explicitEnd = explicitEndAt(eventData);

  return explicitEnd > 0 ? explicitEnd : timestampField(eventData, "end_timestamp");
}

function earlyJoinWindowSeconds(eventData) {
  if (!isTruthyFlag(eventData, "allow_early_join")) {
    return 0;
  }

  const minutes = Number(eventData?.early_join_minutes ?? 0);
  if (!Number.isFinite(minutes) || minutes <= 0) {
    return 0;
  }

  return minutes * SECONDS_PER_MINUTE;
}

function bufferSeconds(eventData, field) {
  const minutes = Number(eventData?.[field] ?? 0);
  if (!Number.isFinite(minutes) || minutes <= 0) {
    return 0;
  }

  return Math.min(minutes, MAX_CHAT_BUFFER_MINUTES) * SECONDS_PER_MINUTE;
}

function resolvePhase(eventData, now) {
  if (hasExplicitlyEnded(eventData, now)) {
    return PHASE_ENDED;
  }

  if (isTruthyFlag(eventData, "always_available")) {
    return PHASE_LIVE;
  }

  const start = timestampField(eventData, "start_timestamp");
  const end = timestampField(eventData, "end_timestamp");

  if (end > 0 && now >= end) {
    return PHASE_ENDED;
  }

  if (start <= 0) {
    return end > 0 ? PHASE_LIVE : PHASE_PRE;
  }

  if (now >= start) {
    return PHASE_LIVE;
  }

  if (now >= start - earlyJoinWindowSeconds(eventData)) {
    return PHASE_EARLY;
  }

  return PHASE_PRE;
}

function canJoinVideo() {
  return true;
}

function isChatOpen(eventData, now, phase = null) {
  if (!isTruthyFlag(eventData, "hide_chat_outside_meeting")) {
    return true;
  }
  const resolvedPhase = phase ?? resolvePhase(eventData, now);

  if (resolvedPhase === PHASE_LIVE) {
    return true;
  }

  if (resolvedPhase === PHASE_ENDED) {
    const afterSeconds = bufferSeconds(eventData, "chat_open_after_minutes");
    if (afterSeconds <= 0) {
      return false;
    }

    return now < endedAt(eventData) + afterSeconds;
  }

  const beforeSeconds = bufferSeconds(eventData, "chat_open_before_minutes");
  if (beforeSeconds <= 0) {
    return false;
  }

  const start = timestampField(eventData, "start_timestamp");
  if (start <= 0) {
    return false;
  }

  return now >= start - beforeSeconds;
}

function secondsUntilStart(eventData, now) {
  const start = timestampField(eventData, "start_timestamp");
  if (start <= 0 || now >= start) {
    return 0;
  }

  return start - now;
}

function resolve(eventData, now) {
  const phase = resolvePhase(eventData, now);

  return {
    phase,
    canJoinVideo: canJoinVideo(phase),
    isChatOpen: isChatOpen(eventData, now, phase),
    secondsUntilStart: secondsUntilStart(eventData, now),
  };
}

module.exports = {
  KEY_SERIES_COMPLETED_AT,
  PHASE_PRE,
  PHASE_EARLY,
  PHASE_LIVE,
  PHASE_ENDED,
  MAX_CHAT_BUFFER_MINUTES,
  resolve,
  resolvePhase,
  canJoinVideo,
  isChatOpen,
  secondsUntilStart,
};
